import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";

export abstract class UndoRedoHandler<TState> {
  private updatingState = false;
  private $undoChanges: BehaviorSubject<TState[]> = new BehaviorSubject<
    TState[]
  >([]);
  private $redoChanges: BehaviorSubject<TState[]> = new BehaviorSubject<
    TState[]
  >([]);
  private $currentChange;

  canUndoChange(): Observable<boolean> {
    return this.$undoChanges.pipe(map((data) => !!data.length));
  }

  canRedoChange() {
    return this.$redoChanges.pipe(map((data) => !!data.length));
  }

  snapped(func: (...args: any[]) => any) {
    if (this.updatingState) return func();

    this.$redoChanges.next([]);
    const undoChanges = this.$undoChanges.value;
    const change = this.getState();
    undoChanges.push(change);
    this.$undoChanges.next(undoChanges);
    func();
    this.$currentChange = this.getState();
  }

  undoChange() {
    if (!this.$undoChanges.value.length) return;

    const undoChanges = this.$undoChanges.value;
    const newState = undoChanges.pop();
    this.$undoChanges.next(undoChanges);

    const redoChanges = this.$redoChanges.value;
    redoChanges.push(this.$currentChange);
    this.$redoChanges.next(redoChanges);
    this.$currentChange = newState;

    this.updatingState = true;
    this.applyState(newState);
    this.updatingState = false;
  }

  redoChange() {
    if (!this.$redoChanges.value.length) return;

    const redoChanges = this.$redoChanges.value;
    const newState = redoChanges.pop();
    this.$redoChanges.next(redoChanges);

    const undoChanges = this.$undoChanges.value;
    undoChanges.push(this.$currentChange);
    this.$undoChanges.next(undoChanges);
    this.$currentChange = newState;

    this.updatingState = true;
    this.applyState(newState);
    this.updatingState = false;
  }

  dropChanges() {
    this.$undoChanges.next([]);
    this.$redoChanges.next([]);
  }

  protected abstract applyState(state: TState);
  protected abstract getState(): TState;
}
